import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils";
import type { VobReadyGroup } from "@/types/vob-ready";

type Props = {
  group: VobReadyGroup | null;
  open: boolean;
  running: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (blockDetails: string) => void | Promise<void>;
};

export function VobBlockDialog({
  group,
  open,
  running,
  onOpenChange,
  onConfirm,
}: Props) {
  const [blockDetails, setBlockDetails] = useState("");

  useEffect(() => {
    if (!open) {
      setBlockDetails("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Block VOB package</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              {group ? (
                <>
                  <p>
                    Block VOB generation and delivery for{" "}
                    <span className="font-medium text-foreground">
                      {group.patient_name || "this patient"}
                    </span>{" "}
                    · {group.j_code} · {group.date} ({group.case_count} case
                    {group.case_count === 1 ? "" : "s"}).
                  </p>
                  <p>
                    This is permanent. The batch will disappear from VOB Ready,
                    customer case APIs will not return a VOB document URL, and
                    you will need engineering support to undo a block.
                  </p>
                </>
              ) : (
                <p>Select a VOB batch to block.</p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <label
            htmlFor="vob-block-details"
            className="text-sm font-medium text-foreground"
          >
            Block note
          </label>
          <textarea
            id="vob-block-details"
            className={cn(
              "flex min-h-[96px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm",
              "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
            value={blockDetails}
            onChange={(event) => setBlockDetails(event.target.value)}
            placeholder="Optional note attached to each case's structured output"
            disabled={running || !group}
          />
          <p className="text-xs text-muted-foreground">
            This note is saved on each case and returned in
            benefits_verification_result.block_details.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={running}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={running || !group}
            onClick={() => void onConfirm(blockDetails.trim())}
          >
            {running ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Blocking…
              </>
            ) : (
              "Block package"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
